import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCheck } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { FeedPostInput } from '@components/Feed/FeedPostInput';
import { FilteredActivityFeedTypes } from '@components/Feed/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  MenuItemLabel,
} from '@components/basics/Dropdowns/GlobalDropdown';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { ActivityFeedGroupFilterOption } from '@graphql/reactQuery';

type FeedType = {
  type: ActivityFeedGroupFilterOption;
  label: string;
  icon?: React.ReactNode;
}[];

const FEED_TYPES = (authenticated: boolean, feedFilter: FilteredActivityFeedTypes): FeedType => [
  {
    type: ActivityFeedGroupFilterOption.All,
    label: 'All',
  },
  {
    type: ActivityFeedGroupFilterOption.Posts,
    label: 'Posts',
  },
  {
    type: ActivityFeedGroupFilterOption.Collected,
    label: 'Collected',
  },

  ...(feedFilter === FilteredActivityFeedTypes.User || authenticated
    ? [
        {
          type: ActivityFeedGroupFilterOption.ReleaseDropped,
          label: 'Releases',
        },
      ]
    : []),
];

type UserType = {
  type: FilteredActivityFeedTypes;
  label: string;
}[];

const FEED_USER_TYPES = (): UserType => [
  {
    type: FilteredActivityFeedTypes.NotableCollectors,
    label: 'For You',
  },
  {
    type: FilteredActivityFeedTypes.Global,
    label: 'Trending',
  },
  {
    type: FilteredActivityFeedTypes.User,
    label: 'Following',
  },
];

export const FeedHeader = ({
  activityFeedFilter,
  setActivityFeedFilter,
  activityFeedGroupFilter,
  setActivityFeedGroupFilter,
  loading,
}: {
  activityFeedFilter: FilteredActivityFeedTypes;
  setActivityFeedFilter: (selection: FilteredActivityFeedTypes) => void;
  activityFeedGroupFilter: ActivityFeedGroupFilterOption[];
  setActivityFeedGroupFilter: (selection: ActivityFeedGroupFilterOption[]) => void;
  loading: boolean;
}) => {
  const { authToken } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const { mobile } = useMediaQuery();

  const feedTypes = useMemo(() => {
    return FEED_TYPES(!!authToken, activityFeedFilter);
  }, [authToken, activityFeedFilter]);

  const dropdownTriggerText = useMemo(() => {
    if (activityFeedGroupFilter.length === 0) {
      return 'All';
    } else if (activityFeedGroupFilter.length === 1) {
      const label = feedTypes.find(feedType => feedType.type === activityFeedGroupFilter[0])?.label;
      if (label) {
        return label;
      }
      // If we don't find a label, reset the filter
      setActivityFeedGroupFilter([ActivityFeedGroupFilterOption.All]);
      return;
    } else {
      return 'Multiple';
    }
  }, [activityFeedGroupFilter, feedTypes, setActivityFeedGroupFilter]);

  const dropdown = (
    <DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
      <DropdownMenuTrigger>
        <div className="flex h-7 items-center gap-2 rounded-md px-3 text-base-m font-medium text-base800 hover:bg-neutral100">
          {dropdownTriggerText}
          <FontAwesomeIcon
            icon={faChevronDown}
            size="1x"
            className={`transition-all ${isDropdownOpen ? `rotate-180` : ``}`}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {feedTypes.map(feedType => (
          <DropdownMenuItem
            key={feedType.type}
            onClick={() => {
              setActivityFeedGroupFilter([feedType.type]);
            }}
          >
            <div className="flex gap-2">
              <div className="w-3 flex-00auto">
                {(activityFeedGroupFilter.includes(feedType.type) ||
                  (activityFeedGroupFilter.length === 0 && feedType.type === 'ALL')) && (
                  <FontAwesomeIcon icon={faCheck} size="sm" />
                )}
              </div>

              <MenuItemLabel className="flex-1">{feedType.label}</MenuItemLabel>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="mb-7 mt-4 flex flex-col gap-5">
      <div className="flex h-9 w-full border-b border-neutral100 lg:h-10">
        <div className="flex flex-00auto gap-5 lg:gap-8">
          {!loading && (
            <>
              {FEED_USER_TYPES().map(feedType => (
                <div
                  onClick={() => {
                    setActivityFeedFilter(feedType.type);
                    setActivityFeedGroupFilter([ActivityFeedGroupFilterOption.Posts]);
                  }}
                  key={feedType.type}
                  className={`flex-00auto cursor-pointer font-title text-title-m font-medium lg:text-title-l ${
                    activityFeedFilter === feedType.type
                      ? `border-b-3 border-base800 text-base800`
                      : `text-neutral500`
                  }`}
                >
                  {feedType.label}
                </div>
              ))}
            </>
          )}
        </div>
        {mobile && <div className="flex-1" />}
        {mobile && <div className="self-start">{!loading && dropdown}</div>}
      </div>
      {!mobile && <div className="self-end">{!loading && dropdown}</div>}
      <FeedPostInput />
    </div>
  );
};
